import React, { memo, useCallback, useRef } from "react";
import ProposalRoomView from "./ProposalRoomView";

interface ProposalRoomWrapperProps {
  proposalId: number;
  rmcClientMappingId?: number;
  propertyName: string;
  onRoomDataUpdate: (
    propertyId: number,
    selectedRooms: number[],
    roomCharges: Record<number, { amount: number; taxPercentage?: number }>,
    roomComplementaries: Record<number, number[]>
  ) => void;
  selectedPropertyId: number;
  isViewMode?: boolean;
  onBack?: () => void;
  initialRoomData?: any;
  currencySymbol?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onPropertyAutoSelect?: () => void;
  onPropertyAutoDeselect?: () => void;
}

const ProposalRoomWrapper: React.FC<ProposalRoomWrapperProps> = memo(
  ({
    proposalId,
    rmcClientMappingId,
    propertyName,
    onRoomDataUpdate,
    selectedPropertyId,
    isViewMode = false,
    onBack,
    initialRoomData,
    currencySymbol,
    isExpanded,
    onToggleExpand,
    onPropertyAutoSelect,
    onPropertyAutoDeselect,
  }) => {
    // Use refs to store all values that need to be stable
    const stableCallbackRef = useRef(onRoomDataUpdate);
    const selectedPropertyIdRef = useRef(selectedPropertyId);

    stableCallbackRef.current = onRoomDataUpdate;
    selectedPropertyIdRef.current = selectedPropertyId;

    // Create a completely stable callback that never changes
    const handleRoomDataChangeInternal = useCallback(
      (
        selectedRooms: number[],
        roomCharges: Record<number, { amount: number; taxPercentage?: number }>,
        roomComplementaries: Record<number, number[]>
      ) => {
        if (stableCallbackRef.current) {
          stableCallbackRef.current(
            selectedPropertyIdRef.current,
            selectedRooms,
            roomCharges,
            roomComplementaries
          );
        }
      },
      [] // No dependencies - completely stable
    );

    return (
      <ProposalRoomView
        propertyId={selectedPropertyId}
        propertyName={propertyName}
        proposalId={proposalId}
        clientPropertyMappingId={undefined}
        rmcClientMappingId={rmcClientMappingId || 0}
        readOnlyMode={isViewMode}
        currencySymbol={currencySymbol}
        onBack={onBack}
        onRoomDataChange={handleRoomDataChangeInternal}
        initialRoomData={initialRoomData}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onPropertyAutoSelect={onPropertyAutoSelect}
        onPropertyAutoDeselect={onPropertyAutoDeselect}
      />
    );
  }
);

ProposalRoomWrapper.displayName = "ProposalRoomWrapper";

export default ProposalRoomWrapper;
